import {
  CroptypeColor,
  CroptypePlanning,
  CroptypePlanningSettings,
  Factory,
  FactoryConsumption,
  ISettingsConfig,
  TruckSpeed,
} from '@agroone/entities'
import { HttpResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { NetworkingService } from '../../networking/services/networking.service'
import { RegionService } from '../region/region.service'

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  public settingsSubject: BehaviorSubject<ISettingsConfig> = new BehaviorSubject<ISettingsConfig>({
    trucks: [],
    destinations: [],
    stocks: [],
    croptypePlannings: [],
    factoryConsumptions: [],
    harvestTeams: [],
    croptypeColors: [],
    truckSpeeds: [],
    harvesters: [],
  })
  public settings$: Observable<ISettingsConfig> = this.settingsSubject.asObservable()

  public get croptypeColors(): CroptypeColor[] {
    return this.getValue().croptypeColors ?? []
  }

  public get croptypePlannings(): CroptypePlanningSettings[] {
    return this.getValue().croptypePlannings ?? []
  }

  public get factoryConsumptions(): FactoryConsumption[] {
    return this.getValue().factoryConsumptions ?? []
  }

  public get destinations(): Factory[] {
    return this.getValue().destinations?.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')) ?? []
  }

  public get truckSpeeds(): TruckSpeed[] {
    return this.getValue().truckSpeeds ?? []
  }

  private readonly SETTINGS_URL = `${environment.apiUrl}${environment.settings}`

  private DEFAULT_TRUCK_SPEED = 60

  private network = inject(NetworkingService)
  private regionService = inject(RegionService)

  async init(): Promise<ISettingsConfig> {
    return firstValueFrom(this.get())
  }

  public getTruckSpeedByRegion(region: string): number {
    const regionId = this.regionService.currentRegions?.find((r) => r.name === region)?.id
    const truckSpeed: TruckSpeed | undefined = this.truckSpeeds
      ? this.truckSpeeds.find((t) => t.regionId === regionId)
      : undefined

    return truckSpeed?.speed ?? this.DEFAULT_TRUCK_SPEED
  }

  public getValue(): ISettingsConfig {
    return this.settingsSubject.getValue()
  }

  public saveCroptypePlanning(body: CroptypePlanning): Promise<HttpResponse<CroptypePlanning>> {
    return this.network.put(`${this.SETTINGS_URL}/croptype-plannings`, body)
  }

  public saveFactoryConsumption(body: FactoryConsumption): Promise<HttpResponse<FactoryConsumption>> {
    return this.network.put(`${this.SETTINGS_URL}/factory-consumptions`, body)
  }

  public get(): Observable<ISettingsConfig> {
    return (this.network.get(this.SETTINGS_URL) as Observable<ISettingsConfig>).pipe(
      tap((value: ISettingsConfig) => {
        value.destinations = (value.destinations ?? []).map((d) => new Factory(d))
        this.settingsSubject.next(value)
      }),
    )
  }
}
